/**
 * <brio-code-example> -- progressive code block with copy + source toggle.
 *
 * Markup (minimum):
 *   <brio-code-example>
 *     <pre class="code"><code>...source...</code></pre>
 *   </brio-code-example>
 *
 * Without JS, the <pre class="code"> renders as a normal visible code
 * block. With JS, the element adds a header bar with a copy-to-clipboard
 * button and a source show/hide toggle; source is collapsed by default
 * unless the `open` attribute is present.
 *
 * Attributes:
 *   label     -- language label in the header (default "HTML")
 *   open      -- if present, the source block starts expanded
 *   copy-only -- if present, the source is always shown and cannot be
 *               collapsed; the built header carries only the copy
 *               button (no source toggle)
 *   variant   -- code-header style variant: "filled", "accent", "merged",
 *               "minimal", or "floating"
 *
 * Custom chrome: author a <template data-header> inside the element
 * and its first element child replaces the built header -- swap the
 * icons, the buttons, or the whole bar. Hooks inside the cloned
 * markup: [data-label] receives the label text, [data-copy] and
 * [data-toggle] are wired to the copy and source-toggle behavior.
 * Copy feedback on custom chrome is a data-copied attribute set for
 * ~1.5s (style it with [data-copied]); the built header also swaps its
 * icon and text in that window. The built chrome carries the same
 * hooks and .code-header-label / .code-action-label classes, so one
 * contract covers both paths.
 *
 * Primary use case is auto-injection from the docs build (see
 * scripts/docs.mjs), which wraps the demo source between
 * code:start/code:end markers. Can also be authored by hand.
 *
 * The built header chrome is constructed with DOM APIs --
 * createElement, no innerHTML, no markup strings (P9). Icon geometry
 * lives in small shape specs (tag + attributes) instantiated by
 * svgIcon(); the attribute values are data, not parsed markup.
 */

import { BrioBase } from './brio-base.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

const ICONS = {
    copy: [
        ['rect', { x: 9, y: 9, width: 13, height: 13, rx: 2, ry: 2 }],
        ['path', { d: 'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' }],
    ],
    check: [['polyline', { points: '20 6 9 17 4 12' }]],
    code: [
        ['polyline', { points: '16 18 22 12 16 6' }],
        ['polyline', { points: '8 6 2 12 8 18' }],
    ],
};

const COPY_RESET_MS = 1500;

function svgIcon(shapes) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    for (const [tag, attrs] of shapes) {
        const shape = document.createElementNS(SVG_NS, tag);
        for (const [name, value] of Object.entries(attrs)) {
            shape.setAttribute(name, value);
        }
        svg.append(shape);
    }
    return svg;
}

function actionLabel(text) {
    const span = document.createElement('span');
    span.className = 'code-action-label';
    span.textContent = text;
    return span;
}

class BrioCodeExample extends BrioBase {
    static tag = 'brio-code-example';
    static {
        customElements.define(BrioCodeExample.tag, BrioCodeExample);
    }

    #pre;
    #copyBtn;
    #toggleBtn;
    #copyOnly;
    #builtChrome;
    #onClick;

    connectedCallback() {
        this.#pre = this.$('pre');
        if (!this.#pre) return;

        const label = this.getAttribute('label') || 'HTML';
        this.#copyOnly = this.hasAttribute('copy-only');
        const startOpen = this.#copyOnly || this.hasAttribute('open');
        const variant = this.getAttribute('variant');

        // Authored template replaces the built chrome; the built chrome
        // carries the same data-* hooks, so wiring is shared.
        const tpl = this.$('template[data-header]');
        const header = tpl ? tpl.content.cloneNode(true).firstElementChild : this.#buildHeader();
        if (!header) return;
        this.#builtChrome = !tpl;
        if (variant) header.classList.add(`is-${variant}`);

        const labelEl = header.querySelector('[data-label]');
        if (labelEl) labelEl.textContent = label;

        this.#copyBtn = header.querySelector('[data-copy]');
        this.#toggleBtn = header.querySelector('[data-toggle]');

        this.insertBefore(header, this.#pre);

        if (!startOpen) this.#pre.hidden = true;
        if (this.#toggleBtn) this.#toggleBtn.setAttribute('aria-expanded', String(startOpen));

        this.#onClick = (e) => {
            const btn = e.target.closest('button');
            if (!btn) return;
            if (btn === this.#copyBtn) this.#copy();
            if (btn === this.#toggleBtn) this.#toggle();
        };
        this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
    }

    #buildHeader() {
        const header = document.createElement('div');
        header.className = 'code-header';

        const labelEl = document.createElement('span');
        labelEl.className = 'code-header-label';
        labelEl.setAttribute('data-label', '');

        const actions = document.createElement('div');
        actions.className = 'code-header-actions';

        const copyBtn = this.#actionButton(ICONS.copy, 'Copy');
        copyBtn.setAttribute('data-copy', '');
        actions.append(copyBtn);

        // copy-only locks the source open, so the toggle has nothing
        // to do and stays out of the header.
        if (!this.#copyOnly) {
            const toggleBtn = this.#actionButton(ICONS.code, 'Source');
            toggleBtn.setAttribute('data-toggle', '');
            actions.append(toggleBtn);
        }

        header.append(labelEl, actions);
        return header;
    }

    #actionButton(shapes, text) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'button is-sm is-ghost';
        btn.setAttribute('aria-label', text);
        btn.append(svgIcon(shapes), actionLabel(text));
        return btn;
    }

    async #copy() {
        const code = this.$('code');
        if (!code) return;

        try {
            await navigator.clipboard.writeText(code.textContent);
            this.#setCopied(true);
            setTimeout(() => this.#setCopied(false), COPY_RESET_MS);
        } catch {
            // Clipboard permission denied or API unavailable; fail silently.
        }
    }

    #setCopied(copied) {
        // data-copied is the universal feedback hook (built and custom
        // chrome alike); the built button also swaps its icon and text.
        this.#copyBtn.toggleAttribute('data-copied', copied);
        if (!this.#builtChrome) return;

        const icon = copied ? ICONS.check : ICONS.copy;
        const text = copied ? 'Copied!' : 'Copy';
        this.#copyBtn.replaceChildren(svgIcon(icon), actionLabel(text));
        this.#copyBtn.setAttribute('aria-label', text);
    }

    #toggle() {
        if (this.#copyOnly) return;
        const expanded = this.#pre.hidden;
        this.#pre.hidden = !expanded;
        this.#toggleBtn.setAttribute('aria-expanded', String(expanded));
    }
}
